// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { HTMLInputTypeAttribute } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Box, TextField, TextFieldProps, Typography } from '@mui/material';
import { FormControlLabel } from './FormControlLabel';
import useStyles from './styles';

interface TextInputProps extends TextFieldProps<'standard'> {
  label: string;
  value: string;
  enableTopLabel?: boolean;
  setValue: (value: string) => void;
  type?: HTMLInputTypeAttribute;
  validationMessage?: string;
  placeholder?: string;
  setValidationMessage?: React.Dispatch<React.SetStateAction<string>>;
  getValidationMessage?: (value: string) => string;
  getValidationMessageAsync?: (value: string) => Promise<string>;
  caption?: string;
}

const TextInput = ({
  label,
  value,
  setValue,
  type,
  validationMessage,
  setValidationMessage,
  getValidationMessage,
  getValidationMessageAsync,
  required,
  disabled,
  hidden,
  enableTopLabel,
  placeholder,
  caption,
  ...textFieldProps
}: TextInputProps) => {
  const { classes } = useStyles();
  const { showBoundary } = useErrorBoundary<Error>();

  if (hidden) {
    return null;
  }

  return (
    <>
      {enableTopLabel && <FormControlLabel required={required} label={label} />}
      {caption && (
        <Typography variant="caption" color="textSecondary">
          {caption}
        </Typography>
      )}

      <TextField
        size="small"
        variant="outlined"
        required={required}
        error={!!validationMessage}
        InputLabelProps={{ className: classes.inputFieldLabel }}
        className={classes.inputField}
        fullWidth
        placeholder={placeholder || label}
        type={type}
        value={value}
        onChange={(e) => {
          if (disabled) return;

          setValue(e.target.value);

          if (setValidationMessage && getValidationMessage) {
            setValidationMessage(getValidationMessage(e.target.value));
          }

          if (setValidationMessage && getValidationMessageAsync) {
            getValidationMessageAsync(e.target.value)
              .then((x) => setValidationMessage(x ?? ''))
              .catch((error: Error) => showBoundary(error));
          }
        }}
        FormHelperTextProps={{ className: classes.inputValidationMessage }}
        helperText={validationMessage}
        disabled={disabled}
        {...textFieldProps}
      />
      {enableTopLabel && <Box sx={{ pb: 2 }} />}
    </>
  );
};

export default TextInput;
